import { Navigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import type { AdminUserRole } from '@/types/user';

interface ProtectedRouteProps {
  children: React.ReactNode;
  permission?: string;
  role?: AdminUserRole | AdminUserRole[];
  organization_id?: string;
}

export function ProtectedRoute({
  children,
  permission,
  role,
  organization_id,
}: ProtectedRouteProps) {
  const { isAuthenticated, hasPermission, hasRole, canAccessOrganization } =
    useAuth();

  if (!isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  if (permission && !hasPermission(permission)) {
    return (
      <div data-testid="route-permission-denied" className="p-4">
        <h2 className="text-lg font-medium">Access Denied</h2>
        <p className="text-muted-foreground">
          You don't have permission to access this page.
        </p>
      </div>
    );
  }

  if (role && !hasRole(role)) {
    return <Navigate to="/" replace />;
  }

  if (organization_id && !canAccessOrganization(organization_id)) {
    return <Navigate to="/" replace />;
  }

  return (
    <div
      data-testid={`protected-route-${permission || (Array.isArray(role) ? role.join('-') : role) || 'default'}`}
    >
      {children}
    </div>
  );
}
